import { useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { FormGroup } from "reactstrap";
import { Box, Button, ButtonColors, Typography } from "@talentech/components";
import { Question } from "@/types/feedback.types";
import { getQuestionText } from "@/utils/formUtils";
import { useTranslation } from "react-i18next";
import useFormLanguage from "@/utils/useFormLanguage.hook";

const MAX_STARS_RATING = 5;

const QuestionStarRating = (props: Question) => {
  const { id, required, scale } = props;

  const formLanguageCode = useFormLanguage();
  const label = getQuestionText(props, formLanguageCode);

  const { control } = useFormContext();

  const fieldName = id.toString();

  const { t } = useTranslation();
  const stars = useMemo(
    () => Array.from({ length: MAX_STARS_RATING }, (_, index) => index),
    [],
  );

  const scaleLabels = useMemo(() => {
    const translatedScale = scale?.translations?.find(
      ({ languageId }) => Number(languageId) === Number(formLanguageCode),
    );

    return {
      leftText:
        translatedScale?.minText ||
        scale?.minText ||
        t("Form.StarRatingField.LeftText"),
      rightText:
        translatedScale?.maxText ||
        scale?.maxText ||
        t("Form.StarRatingField.RightText"),
    };
  }, [formLanguageCode, scale, t]);

  const isStarFilled = (starIndex: number, value: number) => {
    return starIndex < value;
  };

  const onStarClick = (
    starIndex: number,
    onChange: (value: string) => void,
  ) => {
    const newRating = starIndex + 1;
    onChange(String(newRating));
  };

  return (
    <FormGroup
      data-cy="question-star-rating"
      tag="fieldset"
      noMargin
      className="w-100 d-flex flex-column gap-0 p-4 rounded-3 bg-white"
    >
      <span className="fs-6 fw-semibold text-break mb-3 text-300">
        {label}
        {required && <span className="text-danger d-none">&nbsp;*</span>}
      </span>

      <Box flex className="flex-column gap-2">
        <Controller
          {...{ control }}
          name={fieldName}
          render={({ field }) => (
            <Box
              flex
              rounded
              className="flex-column align-items-start align-self-stretch bg-gray-100 gap-2 p-3 p-sm-4 pb-sm-3"
            >
              <Box
                flex
                className="justify-content-between align-items-start align-self-stretch"
              >
                {stars.map((_, index) => (
                  <Button
                    key={index}
                    onClick={() => onStarClick(index, field.onChange)}
                    color={ButtonColors.TRANSPARENT}
                    className="p-0"
                    aria-label={t(
                      `Form.StarRatingField.AriaLabels.${index + 1}`,
                    )}
                    icon={
                      isStarFilled(index, Number(field.value ?? 0)) ? (
                        <i className="fa-sharp fa-solid fa-star fs-2 text-300"></i>
                      ) : (
                        <i className="fa-sharp fa-light fa-star fs-2 text-300"></i>
                      )
                    }
                  />
                ))}
              </Box>

              <Box
                flex
                className="justify-content-between align-items-start align-self-stretch"
              >
                <Typography className="fs-12 m-0 text-300">
                  {scaleLabels.leftText}
                </Typography>

                <Typography className="fs-12 m-0 text-300">
                  {scaleLabels.rightText}
                </Typography>
              </Box>
            </Box>
          )}
        />
      </Box>
    </FormGroup>
  );
};

export default QuestionStarRating;
